"use client"

import { useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { deleteAgent, type DeleteAgentResponse } from "@/lib/backend/builds"
import { getErrorMessage, showErrorToast } from "@/lib/toast-store"

type DeleteState = "idle" | "loading" | "done" | "error"

interface DeleteAgentOptions {
  redirectTo?: string
  refresh?: boolean
  onSuccess?: (result: DeleteAgentResponse) => void
  onError?: (message: string) => void
}

export function useDeleteAgent() {
  const [states, setStates] = useState<Record<string, DeleteState>>({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<DeleteAgentResponse | null>(null)
  const router = useRouter()
  const [, startTransition] = useTransition()

  const handleDelete = async (
    agentRowId: string,
    redirectOrOptions?: string | DeleteAgentOptions
  ): Promise<DeleteAgentResponse | null> => {
    const options =
      typeof redirectOrOptions === "string"
        ? { redirectTo: redirectOrOptions }
        : (redirectOrOptions ?? {})
    const {
      redirectTo,
      refresh = true,
      onSuccess,
      onError,
    } = options

    setErrorMessage(null)
    setStates((prev) => ({ ...prev, [agentRowId]: "loading" }))

    try {
      const result = await deleteAgent(agentRowId)
      setLastResult(result)
      setStates((prev) => ({ ...prev, [agentRowId]: "done" }))
      onSuccess?.(result)

      if (redirectTo) {
        router.push(redirectTo)
        return result
      }

      if (refresh) {
        startTransition(() => {
          router.refresh()
        })
      }

      return result
    } catch (err) {
      const message = getErrorMessage(err, "Delete failed")
      setErrorMessage(message)
      setStates((prev) => ({ ...prev, [agentRowId]: "error" }))
      showErrorToast({
        id: `delete-agent-failed-${agentRowId}`,
        message: `Delete failed: ${message}`,
      })
      onError?.(message)
      return null
    }
  }

  const getState = (agentRowId: string): DeleteState =>
    states[agentRowId] ?? "idle"

  return { handleDelete, getState, errorMessage, setErrorMessage, lastResult }
}
